import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CloudService, EventBridgeRule, ServiceCardData } from '../../core/services/cloud.service';
import { ToastService } from '../../core/services/toast.service';
import { MonitoringService } from '../../core/services/monitoring.service';
import {
  CloudFilters,
  FacetDef,
  SortOption,
  parseFiltersFromParams,
  filtersToParams,
} from './cloud-filter.model';
import { MetricColumn } from './cloud-metrics-panel.component';
import { ActivityItem, findingsToActivity, sortActivity } from './cloud-activity.model';

type Tab = 'overview' | 'rules' | 'activity';

const HISTORY_METRICS: MetricColumn[] = [
  { key: 'invocations', label: 'Invocations' },
  { key: 'failed',      label: 'Failed'      },
];

const DEFAULT_SORT = 'name-asc';
const FACET_KEYS   = ['state', 'monitoring'];

const SORT_OPTIONS: SortOption[] = [
  { value: 'name-asc',        label: 'Name A → Z'       },
  { value: 'name-desc',       label: 'Name Z → A'       },
  { value: 'monitored-first', label: 'Monitored first'  },
];

const FACETS: FacetDef[] = [
  {
    key: 'state',
    label: 'State',
    options: [
      { value: 'ENABLED',  label: 'Enabled'  },
      { value: 'DISABLED', label: 'Disabled' },
    ],
  },
  {
    key: 'monitoring',
    label: 'Monitoring',
    options: [
      { value: 'monitored',   label: 'Monitored'     },
      { value: 'unmonitored', label: 'Not monitored' },
    ],
  },
];

/**
 * EventBridge monitoring is at the rule level (CloudWatch RuleName dimension) —
 * FailedInvocations and Invocations. The monitored resource identifier is
 * the rule name. Cross-account / archived event delivery is not inspected in
 * this version.
 */
@Component({
  selector: 'app-eventbridge-detail',
  templateUrl: './eventbridge-detail.component.html',
})
export class EventBridgeDetailComponent implements OnInit, OnDestroy {
  selectedTab: Tab = 'overview';
  rules: EventBridgeRule[] = [];
  loading = true;

  filters: CloudFilters = { query: '', facets: {}, sort: DEFAULT_SORT };

  readonly tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'rules',    label: 'Rules'    },
    { id: 'activity', label: 'Activity' },
  ];

  readonly facets      = FACETS;
  readonly sortOptions = SORT_OPTIONS;

  readonly serviceCard: ServiceCardData = { id: 'eventbridge', name: 'EventBridge', icon: 'radio', route: '/cloud/eventbridge', status: 'healthy', metrics: [] };
  readonly historyMetrics = HISTORY_METRICS;

  drawerItemId: string | null = null;
  selectedId: string | null = null;

  activity: ActivityItem[] = [];
  activityLoading = true;
  refreshing = false;

  private sub?: Subscription;
  private routeSub?: Subscription;
  private findingsSub?: Subscription;

  constructor(
    private readonly cloud: CloudService,
    private readonly toast: ToastService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly monitoring: MonitoringService,
  ) {}

  ngOnInit(): void {
    this.cloud.loadItemPoliciesForService('eventbridge').subscribe();
    this.routeSub = this.route.queryParams.subscribe(params => {
      const p = params as Record<string, string>;
      this.filters = parseFiltersFromParams(p, DEFAULT_SORT, FACET_KEYS);
      this.selectedId = p['r'] ?? null;
    });
    this.loadData(false);
  }

  /** Loads the rule list (cache-aware), monitoring state (monitored count), and activity findings. */
  private loadData(bypass: boolean): void {
    this.sub?.unsubscribe();
    this.sub = this.cloud.getEventBridgeRules(bypass).subscribe(rules => {
      this.rules      = rules;
      this.loading    = false;
      this.refreshing = false;
    });
    this.cloud.loadMonitoringState().subscribe();
    this.findingsSub?.unsubscribe();
    this.findingsSub = this.monitoring.getFindings().subscribe(findings => {
      this.activityLoading = false;
      this.activity        = sortActivity(findingsToActivity(findings, 'EVENTBRIDGE'));
    });
  }

  /** Manual refresh — bypass the cache and reload (guarded against double-clicks). */
  refresh(): void {
    if (this.refreshing) return;
    this.refreshing = true;
    this.loadData(true);
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.routeSub?.unsubscribe();
    this.findingsSub?.unsubscribe();
  }

  onActivitySelect(item: ActivityItem): void {
    this.selectedTab = 'rules';
    this.selectResource(item.resourceId);
  }

  // ── Overview counts (standardized: total + monitored, then service-specific) ──

  get totalCount():     number { return this.rules.length; }
  get monitoredCount(): number { return this.rules.filter(r => this.isMonitored(r.name)).length; }
  get enabledCount():   number { return this.rules.filter(r => r.state === 'ENABLED').length; }
  get disabledCount():  number { return this.rules.filter(r => r.state === 'DISABLED').length; }

  // ── Filtered + sorted list ──────────────────────────────────────────────────

  get filteredRules(): EventBridgeRule[] {
    let result = this.rules;

    const q = this.filters.query.toLowerCase().trim();
    if (q) result = result.filter(item => item.name.toLowerCase().includes(q));

    const states = this.filters.facets['state'] ?? [];
    if (states.length) {
      result = result.filter(item => states.includes(item.state));
    }

    const monitoring = this.filters.facets['monitoring'] ?? [];
    if (monitoring.length) {
      result = result.filter(item => {
        const mon = this.isMonitored(item.name);
        return (monitoring.includes('monitored') && mon) || (monitoring.includes('unmonitored') && !mon);
      });
    }

    return this.sorted(result, this.filters.sort);
  }

  private sorted(rules: EventBridgeRule[], sort: string): EventBridgeRule[] {
    return [...rules].sort((a, b) => {
      switch (sort) {
        case 'name-desc':       return b.name.localeCompare(a.name);
        case 'monitored-first': {
          const aM = this.isMonitored(a.name) ? 0 : 1;
          const bM = this.isMonitored(b.name) ? 0 : 1;
          return aM - bM || a.name.localeCompare(b.name);
        }
        default: return a.name.localeCompare(b.name); // name-asc
      }
    });
  }

  onFiltersChange(f: CloudFilters): void {
    this.router.navigate([], {
      relativeTo:          this.route,
      queryParams:         filtersToParams(f, FACET_KEYS),
      queryParamsHandling: 'merge',
      replaceUrl:          true,
    });
  }

  // ── Display helpers ─────────────────────────────────────────────────────────

  typeLabel(rule: EventBridgeRule): string {
    return rule.scheduled ? 'Scheduled' : 'Event pattern';
  }

  stateClass(state: string): string {
    return state === 'ENABLED' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500';
  }

  // ── Monitoring ──────────────────────────────────────────────────────────────

  get quotaUsage() { return this.cloud.getQuotaUsage(); }
  get canMonitorMore(): boolean { return this.cloud.canEnableMoreMonitoring(); }

  isMonitored(ruleName: string): boolean { return this.cloud.isMonitored('eventbridge', ruleName); }

  toggleMonitoring(ruleName: string): void {
    const current = this.cloud.isMonitored('eventbridge', ruleName);
    if (!current && !this.cloud.canEnableMoreMonitoring()) {
      this.toast.show(this.cloud.getQuotaExceededMessage(), 'error');
      return;
    }
    this.cloud.toggleMonitoring('eventbridge', ruleName, !current).subscribe({
      error: err => {
        if (err.status === 403) {
          const msg = err.error?.code === 'QUOTA_EXCEEDED'
            ? this.cloud.getQuotaExceededMessage()
            : 'Only org owners and admins can configure monitoring.';
          this.toast.show(msg, 'error');
        }
      },
    });
  }

  // ── Metric-history drill-down ───────────────────────────────────────────────

  selectResource(ruleName: string): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { r: ruleName }, queryParamsHandling: 'merge', replaceUrl: true });
  }
  closeResource(): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { r: null }, queryParamsHandling: 'merge', replaceUrl: true });
  }

  // ── Item settings drawer ────────────────────────────────────────────────────

  openItemSettings(ruleName: string): void  { this.drawerItemId = ruleName; }
  closeItemSettings(): void                  { this.drawerItemId = null; }

  itemOverrideSummary(ruleName: string): string {
    return this.cloud.getItemOverrideSummary('eventbridge', ruleName);
  }
}
